import { HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SignedUrlObject } from '@shared/models/signedUrlObject.model';
import { StoreStorageUtil } from '@shared/utils/store-storage-util';
import * as dayjs from 'dayjs';
import { Observable, of } from 'rxjs';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export abstract class BaseService {
  public env = { ...environment };
  public envService;
  public headers: HttpHeaders;

  protected constructor() {
    // intentionally empty
  }

  get timezone(): string {
    return dayjs().format('Z');
  }

  get serverUrl() {
    return this.envService.headers.Authorization ? this.env.nestServerUrl : this.env.serverUrl;
  }

  get nestServerUrl() {
    return this.env.nestServerUrl;
  }

  loaderHeaders(ms = null): HttpHeaders {
    let header = new HttpHeaders(this.envService.headers);
    if (ms !== null) {
      header = header.append('x-frontend-spinner-loader', ms.toString());
    }
    return header;
  }

  /**
   * Encode an object to HttpParams
   * eg. this.getParams({key:value})
   * @param query  Object for encoding to HttpParams.
   * @param noBracket  Object for no include bracket.
   * @param arrayToString  Object for adding multiple value into url params.
   * @returns HttpParams with a query.
   */
  protected getParams(query, noBracket = false, arrayToString = false): HttpParams {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });

    for (const key of Object.keys(query)) {
      if (query[key]) {
        if (query[key] instanceof Array && arrayToString && query[key].length > 0) {
          params = params.append(key.toString(), query[key].toString());
        } else if (query[key] instanceof Array) {
          query[key].forEach((item) => {
            params = params.append(noBracket ? `${key.toString()}` : `${key.toString()}[]`, item);
          });
        } else {
          params = params.append(key.toString(), query[key]);
        }
      } else if (query[key] === 0 || query[key] === false) {
        params = params.append(key.toString(), query[key]);
      }
    }
    return params;
  }

  /**
   * Returns the full url of the service.
   * eg. this.getUrl('serviceAppend',{'param1':'Param1','param2':'Param2'}
   * @param serviceUrl  serviceUrl.
   * @param urlParams  Object for replacing all matches in serviceAppend.
   * @returns Full service url
   */
  protected getFullUrl(serviceUrl, urlParams: { [key: string]: string } | null = null, isSubUrl = false): string {
    for (const urlParamsKey in urlParams) {
      if (urlParams[urlParamsKey]) {
        serviceUrl = serviceUrl.replace(new RegExp(`{${urlParamsKey}}`, 'g'), urlParams[urlParamsKey]);
      }
    }

    return isSubUrl ? this.serverUrl + serviceUrl : this.serverUrl + this.envService.url + serviceUrl;
  }

  protected getFullUrlWithStoreNo(
    serviceUrl,
    urlParams: { [key: string]: string } | null = null,
    isSubUrl = false
  ): string {
    const storeNo = StoreStorageUtil.retrieveStoreNo();
    urlParams = { ...urlParams, storeNo };
    return this.getFullUrl(serviceUrl, urlParams, isSubUrl);
  }

  protected getFullUrlWithStoreCode(
    serviceUrl,
    urlParams: { [key: string]: string } | null = null,
    isSubUrl = false
  ): string {
    const storeCode = StoreStorageUtil.retrieveStoreCode();
    urlParams = { ...urlParams, storeCode };
    return this.getFullUrl(serviceUrl, urlParams, isSubUrl);
  }

  protected getFullNestUrl(serviceUrl, urlParams: { [key: string]: string } | null = null, isSubUrl = false): string {
    for (const urlParamsKey in urlParams) {
      if (urlParams[urlParamsKey]) {
        serviceUrl = serviceUrl.replace(new RegExp(`{${urlParamsKey}}`, 'g'), urlParams[urlParamsKey]);
      }
    }

    return isSubUrl ? this.env.nestServerUrl + serviceUrl : this.env.nestServerUrl + this.envService.url + serviceUrl;
  }

  public getUrl() {
    return this.serverUrl + this.envService.url;
  }

  public getNestUrl() {
    return this.nestServerUrl + this.envService.url;
  }

  public getFileUrl(refId: string): Observable<SignedUrlObject> {
    return of({ refId, signedUrl: null } as SignedUrlObject);
  }

  public retrieveStore() {
    return StoreStorageUtil.retrieveStore();
  }
}

export class CustomEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}
